import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useFontsVersion } from '../../hooks/useFontsVersion';
import { pxToPt, lineHeightLabel } from '../../lib/units';

/**
 * ¶ marks beside the paragraphs of a text box that carry their *own* spacing (line spacing, space before / after set on
 * highlighted paragraphs — Phase 50 — or imported from Publisher), so it is obvious which paragraphs differ from the
 * box. Shown whenever the box is selected or being edited; hovering a mark tells the paragraph's values against the
 * box's; the mark itself does nothing (and never steals the click / caret).
 *
 * Positions come from the live DOM (`contentRef`: the editable while editing, the display rendering otherwise), measured
 * in screen px and converted into the object's unrotated frame — so rotated boxes show no marks, and so do boxes drawn
 * by the layout engine (wrap obstacles / Align to Baselines: there are no paragraph elements to read).
 * A paragraph's own font size counts too — that is how a blank line is made taller or shorter (its paragraph mark's size).
 */
const BLOCKS = 'div, p, li';
const hasOwnSpacing = (el) => !!(el.style && (el.style.lineHeight || el.style.marginTop || el.style.marginBottom || el.style.fontSize));
const isBlank = (el) => !/[^\s\u200B]/.test(el.textContent || '') && !el.querySelector('img, table');
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : undefined; };
const fmt = (n) => (Math.round(n * 100) / 100).toString();

// Publisher's words and units: multiples read as "1.5", exact heights as "Exactly 12 pt"; sizes and spaces in points.
const describe = (el, box) => {
  const parts = [];
  const boxLh = box.lineHeight ?? 1.2;
  if (el.style.lineHeight) {
    const lh = /px$/.test(el.style.lineHeight) ? el.style.lineHeight : num(el.style.lineHeight);
    if (lh !== undefined) parts.push(`Line spacing ${lineHeightLabel(lh)} — box uses ${lineHeightLabel(boxLh)}`);
  }
  const mt = num(el.style.marginTop);
  if (mt !== undefined) parts.push(`Space before ${fmt(pxToPt(mt))} pt — box uses ${fmt(pxToPt(box.spaceBefore || 0))} pt`);
  const mb = num(el.style.marginBottom);
  if (mb !== undefined) parts.push(`Space after ${fmt(pxToPt(mb))} pt — box uses ${fmt(pxToPt(box.spaceAfter || 0))} pt`);
  const fs = num(el.style.fontSize);
  if (fs !== undefined) parts.push(`${isBlank(el) ? 'Blank line' : 'Font size'} ${fmt(pxToPt(fs))} pt — box uses ${fmt(pxToPt(box.fontSize || 16))} pt`);
  return parts.join('\n');
};

export const ParagraphMarks = ({ objectRef, contentRef, box, editing, zoom, width, height, rotation = 0, html }) => {
  const [marks, setMarks] = useState([]);
  const fontsVersion = useFontsVersion();
  const raf = useRef(0);

  const measure = () => {
    const wrap = objectRef.current;
    const root = contentRef.current;
    if (!wrap || !root || !root.isConnected || (rotation % 360 !== 0)) { setMarks((m) => (m.length ? [] : m)); return; }
    const wr = wrap.getBoundingClientRect();
    const scaleX = wr.width / Math.max(1, width * zoom); // 1 unless an ancestor is CSS-scaled
    const out = [];
    const blocks = Array.from(root.querySelectorAll(BLOCKS)).filter((b) => hasOwnSpacing(b) && !b.querySelector(BLOCKS) && !b.closest('[data-wp-exclusion]'));
    blocks.forEach((b, i) => {
      const r = b.getBoundingClientRect();
      if (!r.height) return;
      const top = (r.top - wr.top) / scaleX;
      if (top < -4 || top > height * zoom + 4) return; // clipped by the box
      out.push({ key: `${i}-${top.toFixed(1)}`, top: top + Math.min(r.height / scaleX, 18) / 2, title: describe(b, box) });
    });
    setMarks((prev) => (prev.length === out.length && prev.every((m, i) => m.key === out[i].key && m.title === out[i].title && Math.abs(m.top - out[i].top) < 0.5) ? prev : out));
  };
  const measureRef = useRef(measure);
  measureRef.current = measure;
  const schedule = useCallback(() => { if (!raf.current) raf.current = window.requestAnimationFrame(() => { raf.current = 0; measureRef.current(); }); }, []);

  // re-measure on anything that moves the paragraphs: typing / formatting (DOM mutations), fonts, zoom, size, props
  useLayoutEffect(() => { schedule(); });
  useEffect(() => {
    const root = contentRef.current;
    if (!root || typeof MutationObserver === 'undefined') return undefined;
    const mo = new MutationObserver(schedule);
    mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'], characterData: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    ro?.observe(root);
    return () => { mo.disconnect(); ro?.disconnect(); if (raf.current) { window.cancelAnimationFrame(raf.current); raf.current = 0; } };
    // editing / fontsVersion / zoom / width / height / html are the triggers that swap or reflow the observed element
  }, [contentRef, schedule, editing, fontsVersion, zoom, width, height, html]);

  if (!marks.length) return null;
  return (
    <div className="wp-para-marks" aria-hidden="true" data-testid="paragraph-marks" data-count={marks.length}>
      {marks.map((m) => (
        <div
          key={m.key}
          className="wp-para-mark"
          style={{ top: m.top }}
          title={m.title}
          data-testid="paragraph-mark"
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          ¶
        </div>
      ))}
    </div>
  );
};
